/**
 * Refund calculation for parent cancellation (KidHive). Given a booking's SNAPSHOTTED cancellation
 * policy and the cancel time vs. the session start, computes the refund the customer is owed.
 *
 * Reads the FROZEN policy at protectedData.cancellationPolicy (snapshotted at booking time), NEVER
 * the provider's current/live policy -- refunds use the terms in effect when the booking was made.
 * Server-authoritative: the amount is derived from the booking's payinTotal server-side, never
 * trusted from the client (it's money). Process-agnostic: single-session, term-enrollment, and
 * provider-initiated cancellations all reuse this.
 *
 * HOW THE REFUND IS EXECUTED (design; the live wiring is a follow-up):
 *   Sharetribe issues the Stripe refund + reverses the application fee via PROCESS ACTIONS on a
 *   cancel transition -- we never call stripe.refunds.create directly. default-booking has no
 *   customer cancel and only a full-refund action, so parent-cancel is added on a FORKED process:
 *
 *     {:name :transition/customer-cancel
 *      :actor :actor.role/customer
 *      :actions [{:name :action/privileged-set-line-items}  ; refund line items computed server-side
 *                {:name :action/stripe-refund-payment}       ; refunds payin delta + reverses the fee
 *                {:name :action/cancel-booking}]
 *      :from :state/accepted :to :state/cancelled
 *      :privileged? true}                                    ; server sets the amount, never the client
 *
 *   Partial (e.g. 50%) refunds are expressed as refund LINE ITEMS (NOT :action/calculate-full-refund,
 *   which is 100%-only): the privileged transition-privileged handler computes them from
 *   computeRefundForTransaction and sets them, and :action/stripe-refund-payment refunds the delta.
 *
 *   !! NEEDS LIVE VALIDATION (live-Stripe follow-up): that Sharetribe reverses the Stripe application
 *   fee PROPORTIONALLY on a partial refund (refund_application_fee semantics) must be confirmed
 *   against Sharetribe's docs + Stripe test mode, together with the exact refund line-item shape.
 *   This module only computes the refund amount/percentage; it does NOT express the line items, push
 *   the forked process, or call Stripe.
 *
 * All functions pure; mirrored in server/api-util/refundCalculationServer.js -- KEEP IN SYNC.
 */

import { normalizeCancellationPolicy } from './cancellationPolicy';

const HOUR_MS = 3600000;

const isPlainObject = value =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const toMillis = value => {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : t;
  }
  return null;
};

const clampPercentage = pct => {
  const n = typeof pct === 'number' && Number.isFinite(pct) ? pct : 0;
  return Math.min(100, Math.max(0, n));
};

/**
 * Hours between the cancel time and the session start. Positive = cancelling before the session;
 * negative = after it has started. Returns null if either time is unparseable.
 * @param {(Date|string|number)} sessionStart
 * @param {(Date|string|number)} cancelTime
 * @returns {number|null}
 */
export const computeHoursBefore = (sessionStart, cancelTime) => {
  const start = toMillis(sessionStart);
  const cancel = toMillis(cancelTime);
  if (start == null || cancel == null) {
    return null;
  }
  return (start - cancel) / HOUR_MS;
};

/**
 * Refund percentage for a snapshotted policy given how many hours before the session the parent
 * cancels. Walks tiers (normalized, sorted DESC by cutoffHours); the first tier with
 * hoursBefore >= cutoffHours wins (INCLUSIVE boundary). No tier matches (or hoursBefore is
 * null/negative) -> 0%. A missing/empty policy falls to the system default via normalize.
 * @param {Object} policy snapshotted cancellation policy
 * @param {number|null} hoursBefore
 * @returns {number} 0..100
 */
export const resolveRefundPercentage = (policy, hoursBefore) => {
  if (hoursBefore == null) {
    return 0;
  }
  const { tiers } = normalizeCancellationPolicy(policy);
  const match = tiers.find(tier => hoursBefore >= tier.cutoffHours);
  return match ? match.refundPercentage : 0;
};

/**
 * Refund amount (integer subunits) = round(paymentTotalSubunits * refundPercentage / 100).
 * @param {number} paymentTotalSubunits
 * @param {number} refundPercentage
 * @returns {number}
 */
export const computeRefundAmount = (paymentTotalSubunits, refundPercentage) => {
  if (!Number.isInteger(paymentTotalSubunits) || paymentTotalSubunits <= 0) {
    return 0;
  }
  return Math.round((paymentTotalSubunits * clampPercentage(refundPercentage)) / 100);
};

/**
 * Compute the refund for a cancellation.
 * @param {Object} opts
 * @param {Object} [opts.policy] snapshotted cancellation policy
 * @param {(Date|string|number)} [opts.sessionStart]
 * @param {(Date|string|number)} [opts.cancelTime]
 * @param {(Object|number)} [opts.paymentTotal] Money-like { amount, currency } or integer subunits
 * @returns {{ hoursBefore:(number|null), refundPercentage:number, refundAmount:number, currency:(string|null) }}
 */
export const computeRefund = ({ policy, sessionStart, cancelTime, paymentTotal } = {}) => {
  const hoursBefore = computeHoursBefore(sessionStart, cancelTime);
  const refundPercentage = resolveRefundPercentage(policy, hoursBefore);
  const amount = isPlainObject(paymentTotal) ? paymentTotal.amount : paymentTotal;
  const subunits = Number.isInteger(amount) ? amount : 0;
  const currency = isPlainObject(paymentTotal) ? paymentTotal.currency || null : null;
  return {
    hoursBefore,
    refundPercentage,
    refundAmount: computeRefundAmount(subunits, refundPercentage),
    currency,
  };
};

/**
 * The "free-cancel window" = the window where a cancel would be FULLY refunded (refundPercentage ===
 * 100). Pure; reuses resolveRefundPercentage. KEEP IN SYNC with the server mirror in
 * server/api-util/refundCalculationServer.js.
 * @param {Object} opts { policy, sessionStart, atTime }
 * @returns {boolean}
 */
export const isInFreeCancelWindow = ({ policy, sessionStart, atTime } = {}) =>
  resolveRefundPercentage(policy, computeHoursBefore(sessionStart, atTime)) === 100;

/**
 * How close to the session start a parent may still reschedule (client decision, 2026-07-10:
 * "up to 12 hours before the class starts"). A plain time cutoff, deliberately DECOUPLED from the
 * refund policy -- the old free-cancel-window gate hid the reschedule button entirely for strict
 * policies (no 100% window exists) and 7 days out for moderate ones.
 * KEEP IN SYNC with server/api-util/refundCalculationServer.js.
 */
export const RESCHEDULE_CUTOFF_HOURS = 24; // policy v1.3 sec.6: reschedulable within the full-refund window

/** True while the session start is at least RESCHEDULE_CUTOFF_HOURS away. False on unparseable input. */
export const isBeforeRescheduleCutoff = (sessionStart, atTime = new Date()) => {
  const hoursBefore = computeHoursBefore(sessionStart, atTime);
  return hoursBefore != null && hoursBefore >= RESCHEDULE_CUTOFF_HOURS;
};

// ---- Transaction seam (server-authoritative source extraction) ----

const getBookingStart = transaction => {
  // Denormalized booking entity: transaction.booking.attributes.start
  const denorm = transaction?.booking?.attributes?.start;
  if (denorm != null) {
    return denorm;
  }
  // Fallback: some flows stash the session start on protectedData.
  return transaction?.attributes?.protectedData?.bookingStart ?? null;
};

/**
 * Compute the refund for a transaction (cancelTime injectable). Reads ALL inputs off the transaction
 * server-side -- the snapshotted policy (protectedData.cancellationPolicy), what the customer paid
 * (attributes.payinTotal), and the session start (booking.attributes.start). The client supplies
 * none of these, so the amount can't be tampered with.
 * @param {Object} transaction
 * @param {(Date|string|number)} cancelTime
 */
export const computeRefundForTransaction = (transaction, cancelTime) => {
  const attributes = (isPlainObject(transaction) && transaction.attributes) || {};
  return computeRefund({
    policy: attributes.protectedData?.cancellationPolicy,
    sessionStart: getBookingStart(transaction),
    cancelTime,
    paymentTotal: attributes.payinTotal,
  });
};
